import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlassPlus,
  faMagnifyingGlassMinus,
  faRotateRight,
  faRotateLeft,
  faUndo,
  faCheck,
  faXmark,
  faCropSimple,
  faSliders
} from "@fortawesome/free-solid-svg-icons";

const TARGET_WIDTH = 500;
const TARGET_HEIGHT = 200;
const ASPECT_RATIO = TARGET_WIDTH / TARGET_HEIGHT; // 2.5

const ImageCropperModal = ({
  isOpen,
  imageSrc,
  onCropComplete,
  onCancel,
  fileName = "banner_500x200.jpg"
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0); // 0, 90, 180, 270
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [effectiveSrc, setEffectiveSrc] = useState("");
  const [isConverting, setIsConverting] = useState(false);

  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Reset state when modal opens or image changes
  useEffect(() => {
    let isMounted = true;
    if (isOpen && imageSrc) {
      setZoom(1);
      setRotation(0);
      setOffset({ x: 0, y: 0 });
      setImageLoaded(false);

      if (imageSrc.startsWith("blob:") || imageSrc.startsWith("data:")) {
        setEffectiveSrc(imageSrc);
        setIsConverting(false);
      } else {
        setIsConverting(true);
        // Convert remote URL to Blob URL to prevent Canvas Tainting (CORS issue)
        fetch(imageSrc, { mode: "cors" })
          .then((res) => {
            if (!res.ok) throw new Error("Fetch failed");
            return res.blob();
          })
          .then((blob) => {
            if (isMounted) {
              const blobUrl = URL.createObjectURL(blob);
              setEffectiveSrc(blobUrl);
            }
          })
          .catch((err) => {
            console.warn("Direct fetch failed, falling back to original imageSrc", err);
            if (isMounted) {
              setEffectiveSrc(imageSrc);
            }
          })
          .finally(() => {
            if (isMounted) {
              setIsConverting(false);
            }
          });
      }
    }
    return () => {
      isMounted = false;
    };
  }, [isOpen, imageSrc]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - offset.x,
      y: e.clientY - offset.y,
    });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      setOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.0015;
    setZoom((prevZoom) => Math.min(Math.max(prevZoom + delta, 0.5), 3));
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleRotate = (direction) => {
    setRotation((prev) => {
      if (direction === "right") return (prev + 90) % 360;
      return (prev - 90 + 360) % 360;
    });
  };

  const handleReset = () => {
    setZoom(1);
    setRotation(0);
    setOffset({ x: 0, y: 0 });
  };

  const getCroppedImage = () => {
    if (!imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const container = containerRef.current;

    if (!img.naturalWidth || !img.naturalHeight) {
      console.warn("Image not fully loaded yet.");
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const cropWidth = containerRect.width;
    const cropHeight = containerRect.height;
    if (cropWidth <= 0 || cropHeight <= 0) return;

    // Create canvas with exact fixed dimensions 500 x 200
    const canvas = document.createElement("canvas");
    canvas.width = TARGET_WIDTH;
    canvas.height = TARGET_HEIGHT;
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    // Smooth image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Fill background with white/transparent if needed
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);

    // Save context state
    ctx.save();

    // Scale from container size to fixed target size (500x200)
    const scaleToTarget = TARGET_WIDTH / cropWidth;

    // Center of canvas
    ctx.translate(TARGET_WIDTH / 2, TARGET_HEIGHT / 2);

    // Apply translation from offset
    ctx.translate(offset.x * scaleToTarget, offset.y * scaleToTarget);

    // Apply rotation
    ctx.rotate((rotation * Math.PI) / 180);

    // Calculate base dimensions matching DOM object-fit: contain behavior
    const imgAspect = img.naturalWidth / img.naturalHeight;
    let baseW, baseH;

    if (rotation === 90 || rotation === 270) {
      const rotAspect = 1 / imgAspect;
      if (rotAspect > ASPECT_RATIO) {
        baseW = TARGET_WIDTH;
        baseH = TARGET_WIDTH / rotAspect;
      } else {
        baseH = TARGET_HEIGHT;
        baseW = TARGET_HEIGHT * rotAspect;
      }
    } else {
      if (imgAspect > ASPECT_RATIO) {
        baseW = TARGET_WIDTH;
        baseH = TARGET_WIDTH / imgAspect;
      } else {
        baseH = TARGET_HEIGHT;
        baseW = TARGET_HEIGHT * imgAspect;
      }
    }

    const drawW = baseW * zoom;
    const drawH = baseH * zoom;

    // Draw centered
    ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

    ctx.restore();

    try {
      // Convert canvas to blob and file
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error("Canvas conversion to Blob failed.");
            return;
          }
          const croppedFile = new File([blob], fileName, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          const croppedImageUrl = URL.createObjectURL(blob);
          onCropComplete(croppedFile, croppedImageUrl);
        },
        "image/jpeg",
        0.95
      );
    } catch (err) {
      console.error("Error exporting cropped image blob:", err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
              <FontAwesomeIcon icon={faCropSimple} size="lg" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Crop Banner Image</h3>
              <p className="text-xs text-gray-500 font-medium">
                Fixed size: <span className="text-blue-600 font-semibold">500 × 200 px</span> (Aspect Ratio 2.5:1)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 rounded-full bg-gray-100 text-gray-400 hover:text-gray-700 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Crop Viewport */}
        <div className="p-6 flex flex-col items-center justify-center bg-gray-900 overflow-hidden relative select-none">
          {/* Instruction hint */}
          <div className="absolute top-3 left-3 z-10 bg-black/60 backdrop-blur-md text-white text-[11px] px-3 py-1 rounded-full flex items-center gap-1.5 pointer-events-none">
            <FontAwesomeIcon icon={faSliders} className="text-blue-400" />
            <span>Drag image to position • Scroll to zoom</span>
          </div>

          {/* Crop Container Box matching 500x200 aspect ratio */}
          <div
            ref={containerRef}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            className="relative w-full max-w-[620px] aspect-[500/200] border-2 border-blue-500 rounded-xl overflow-hidden cursor-grab active:cursor-grabbing shadow-2xl bg-black/40 flex items-center justify-center"
          >
            {/* Loading Overlay */}
            {(isConverting || !imageLoaded) && (
              <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center gap-3">
                <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs font-semibold text-white">Loading image for cropping...</span>
              </div>
            )}

            {/* Grid Overlay */}
            <div className="absolute inset-0 pointer-events-none grid grid-cols-3 grid-rows-3 z-10">
              <div className="border-r border-b border-white/20"></div>
              <div className="border-r border-b border-white/20"></div>
              <div className="border-b border-white/20"></div>
              <div className="border-r border-b border-white/20"></div>
              <div className="border-r border-b border-white/20"></div>
              <div className="border-b border-white/20"></div>
              <div className="border-r border-white/20"></div>
              <div className="border-r border-white/20"></div>
              <div></div>
            </div>

            {/* Target dimensions label badge inside container */}
            <div className="absolute bottom-2 right-2 z-10 bg-blue-600/90 text-white font-mono text-[10px] font-bold px-2 py-0.5 rounded shadow">
              500 × 200 px
            </div>

            {/* Image Element */}
            {effectiveSrc && (
              <img
                ref={imageRef}
                src={effectiveSrc}
                crossOrigin="anonymous"
                alt="To Crop"
                onLoad={handleImageLoad}
                style={{
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                  transformOrigin: "center center",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  transition: isDragging ? "none" : "transform 0.1s ease-out",
                }}
                className="pointer-events-none transition-transform select-none"
              />
            )}
          </div>
        </div>

        {/* Toolbar Controls */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Zoom Slider */}
            <div className="flex items-center gap-3 flex-1 min-w-[240px]">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(z - 0.1, 0.5))}
                className="w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 flex items-center justify-center transition"
                title="Zoom Out"
              >
                <FontAwesomeIcon icon={faMagnifyingGlassMinus} className="text-xs" />
              </button>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.05"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 accent-blue-600 h-1.5 bg-gray-200 rounded-lg cursor-pointer"
              />
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(z + 0.1, 3))}
                className="w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 flex items-center justify-center transition"
                title="Zoom In"
              >
                <FontAwesomeIcon icon={faMagnifyingGlassPlus} className="text-xs" />
              </button>
              <span className="text-xs font-mono text-gray-500 w-12 font-bold">{Math.round(zoom * 100)}%</span>
            </div>

            {/* Rotation & Reset Buttons */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleRotate("left")}
                className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-100 flex items-center gap-1.5 transition"
                title="Rotate Left"
              >
                <FontAwesomeIcon icon={faRotateLeft} />
                <span>90°</span>
              </button>
              <button
                type="button"
                onClick={() => handleRotate("right")}
                className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-100 flex items-center gap-1.5 transition"
                title="Rotate Right"
              >
                <FontAwesomeIcon icon={faRotateRight} />
                <span>90°</span>
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="px-3 py-1.5 rounded-lg bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300 flex items-center gap-1.5 transition"
                title="Reset crop frame"
              >
                <FontAwesomeIcon icon={faUndo} />
                <span>Reset</span>
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={getCroppedImage}
              disabled={isConverting || !imageLoaded}
              className={`px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold text-sm shadow-md shadow-blue-500/20 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition flex items-center gap-2 ${
                isConverting || !imageLoaded ? "opacity-50 cursor-not-allowed hover:scale-100" : ""
              }`}
            >
              <FontAwesomeIcon icon={faCheck} />
              <span>Apply 500×200 Crop</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropperModal;
